/**
 * CoordinatorsView — one card per coordinator: time per study under the
 * current lens, delegation/team/portal authorization chips, and opportunity
 * gaps (authorized but no logged time). Includes explicit pseudo-cards for
 * unattributed time (blank CRC names) and unmapped CRC names.
 */

import { useMemo } from 'react';
import { Loader2, Users } from 'lucide-react';
import { PageHeader, EmptyState } from '../../ui';
import { useAuth } from '../../contexts/AuthContext';
import { useInsights } from '../contexts';
import { useInsightsData, lensTotal, coordinatorKey, studyKey, UNATTRIBUTED_KEY, CoordinatorRollup } from '../hooks/useInsightsData';
import { LensToggle } from '../components/LensToggle';
import { RangePicker } from '../components/RangePicker';
import { RefreshButton } from '../../ui';
import { DataQualityBanner } from '../components/DataQualityBanner';
import { ViewTabs } from '../components/ViewTabs';
import { CoordinatorCard, CoordinatorStudyLink } from '../components/CoordinatorCard';

export function CoordinatorsView() {
  const { currentSite } = useAuth();
  const { range, from, to, lens, setRange, setCustomRange, setLens } = useInsights();
  const data = useInsightsData(currentSite?.id || null, from, to);

  // studyKey -> display label (nickname > protocol number > raw string).
  const studyLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of data.studies) map.set(`study:${s.id}`, s.study_nickname || s.protocol_number);
    for (const r of data.studyRollups.values()) {
      if (!map.has(r.key)) map.set(r.key, r.raw_name || '(no study)');
    }
    return map;
  }, [data.studies, data.studyRollups]);

  // Authorization links per user id: delegations + team + portal access.
  const linksByUser = useMemo(() => {
    const map = new Map<string, Map<string, CoordinatorStudyLink>>();
    const ensure = (userId: string, protocolId: string): CoordinatorStudyLink => {
      const sk = studyKey(protocolId, '');
      let userMap = map.get(userId);
      if (!userMap) {
        userMap = new Map();
        map.set(userId, userMap);
      }
      let link = userMap.get(sk);
      if (!link) {
        link = {
          studyKey: sk,
          label: studyLabels.get(sk) || 'Unknown study',
          delegated: false,
          delegationPending: false,
          onTeam: false,
          teamRole: null,
          portalCount: 0,
        };
        userMap.set(sk, link);
      }
      return link;
    };

    for (const d of data.delegations) {
      for (const e of d.entries) {
        const link = ensure(e.user_id, d.protocol_id);
        if (e.is_currently_delegated) link.delegated = true;
        else if (e.status === 'pending_cert_review' || e.status === 'pending_signatures') link.delegationPending = true;
      }
    }
    for (const s of data.studies) {
      for (const t of s.team) {
        const link = ensure(t.user_id, s.id);
        link.onTeam = true;
        link.teamRole = t.role || null;
      }
    }
    for (const p of data.portalAccess) {
      if (!p.protocol_id) continue;
      const link = ensure(p.user_id, p.protocol_id);
      link.portalCount += 1;
    }
    return map;
  }, [data.delegations, data.studies, data.portalAccess, studyLabels]);

  // Max per-study lens minutes across every coordinator (bar scaling).
  const maxStudyMinutes = useMemo(() => {
    let max = 0;
    for (const r of data.coordinatorRollups.values()) {
      for (const statusMinutes of r.studyStatusMinutes.values()) {
        max = Math.max(max, lensTotal(statusMinutes, lens));
      }
    }
    return max;
  }, [data.coordinatorRollups, lens]);

  // Card list: every site user who appears in ANY source, plus pseudo-cards
  // for unattributed and unmapped-name buckets. Sorted by lens total desc.
  const cards = useMemo(() => {
    const rows: {
      key: string;
      displayName: string;
      subtitle: string | null;
      rollup: CoordinatorRollup | null;
      links: CoordinatorStudyLink[];
      total: number;
      isUnattributed?: boolean;
      isUnmappedName?: boolean;
    }[] = [];

    const seen = new Set<string>();
    for (const c of data.coordinators) {
      const key = coordinatorKey(c.id, '');
      const rollup = data.coordinatorRollups.get(key) || null;
      const links = [...(linksByUser.get(c.id)?.values() || [])];
      const total = rollup ? lensTotal(rollup.minutesByStatus, lens) : 0;
      // Only show users with SOME footprint: time, mapping, or authorization.
      if (!rollup && links.length === 0 && c.crc_names.length === 0) continue;
      seen.add(key);
      rows.push({
        key,
        displayName: c.full_name || c.email || 'Unknown user',
        subtitle: [c.email, c.crc_names.length > 0 ? `CRC name: ${c.crc_names.map((m) => m.crc_name).join(', ')}` : null]
          .filter(Boolean)
          .join(' · ') || null,
        rollup,
        links,
        total,
      });
    }

    for (const r of data.coordinatorRollups.values()) {
      if (seen.has(r.key)) continue;
      if (r.key === UNATTRIBUTED_KEY) {
        rows.push({
          key: r.key,
          displayName: 'Unattributed',
          subtitle: 'Visits with no CRC name recorded',
          rollup: r,
          links: [],
          total: lensTotal(r.minutesByStatus, lens),
          isUnattributed: true,
        });
      } else if (!r.user_id) {
        const rawName = [...r.raw_names][0] || 'unknown';
        rows.push({
          key: r.key,
          displayName: rawName.charAt(0).toUpperCase() + rawName.slice(1),
          subtitle: 'Unmapped CRC name',
          rollup: r,
          links: [],
          total: lensTotal(r.minutesByStatus, lens),
          isUnmappedName: true,
        });
      } else {
        // r.user_id IS set (crc_name_map row survives) but the user isn't in
        // data.coordinators — soft-deleted or access revoked. Their minutes
        // must not silently disappear from the page.
        rows.push({
          key: r.key,
          displayName: 'Former or removed user',
          subtitle: null,
          rollup: r,
          links: [...(linksByUser.get(r.user_id)?.values() || [])],
          total: lensTotal(r.minutesByStatus, lens),
        });
      }
    }

    rows.sort((a, b) => b.total - a.total || a.displayName.localeCompare(b.displayName));
    return rows;
  }, [data.coordinators, data.coordinatorRollups, linksByUser, lens]);

  // Data-quality aggregates for the banner (same computation as
  // StudiesDashboard — unmapped study names matter here too: they put worked
  // minutes under rawstudy:* keys while authorization links are keyed
  // study:<protocol_id>, which can falsely accuse a coordinator of a gap).
  const quality = useMemo(() => {
    const unmappedCrc = new Set<string>();
    let unattributedMinutes = 0;
    for (const r of data.coordinatorRollups.values()) {
      if (r.key === UNATTRIBUTED_KEY) {
        unattributedMinutes = lensTotal(r.minutesByStatus, 'whole_visit');
      } else if (!r.user_id) {
        r.raw_names.forEach((n) => unmappedCrc.add(n));
      }
    }
    const unmappedStudies = [...data.studyRollups.values()]
      .filter((r) => !r.protocol_id && (r.visit_count > 0 || Object.keys(r.minutesByStatus).length > 0))
      .map((r) => r.raw_name);
    let inFlight = 0;
    let incomplete = 0;
    let overrideMinutes = 0;
    let overrideCount = 0;
    for (const v of data.visits) {
      inFlight += Number(v.in_flight_count || 0);
      incomplete += Number(v.incomplete_count || 0);
      overrideMinutes += Number(v.override_minutes || 0);
      overrideCount += Number(v.override_count || 0);
    }
    return { unattributedMinutes, unmappedCrc: [...unmappedCrc], unmappedStudies, inFlight, incomplete, overrideMinutes, overrideCount };
  }, [data.coordinatorRollups, data.studyRollups, data.visits]);

  const busy = data.loading || data.effortLoading;
  // See StudiesDashboard: an errored range means worked time is UNKNOWN, not
  // zero. The banner stays silent and the gap list makes no accusation.
  const activityKnown = !data.effortError;

  return (
    <div className="px-4 pt-4 pb-6 sm:px-6 lg:px-8 lg:pt-5">
      <div className="mx-auto w-full max-w-[1700px] space-y-4">
        <PageHeader
          title="Operations Insights"
          icon={Users}
          count={cards.length}
          subtitle="Who worked on what, what they're authorized for, and where they could spend time"
        />

        <ViewTabs />

        <div className="flex flex-wrap items-center gap-2">
          <RangePicker range={range} from={from} to={to} onRange={setRange} onCustom={setCustomRange} />
          {/* Shared refresh primitive — see StudiesDashboard for the contract. */}
          <RefreshButton onClick={data.refresh} loading={busy} label="Refresh insights data" />
          <LensToggle lens={lens} onChange={setLens} />
          {busy && <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading" />}
        </div>

        {data.error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {data.error}
          </div>
        )}

        {!busy && activityKnown && (
          <DataQualityBanner
            unattributedMinutes={quality.unattributedMinutes}
            unmappedCrcNames={quality.unmappedCrc}
            unmappedStudyNames={quality.unmappedStudies}
            inFlightCount={quality.inFlight}
            incompleteCount={quality.incomplete}
            overrideMinutes={quality.overrideMinutes}
            overrideCount={quality.overrideCount}
          />
        )}

        {!busy && cards.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No coordinator activity yet"
            hint="Cards appear once coordinators log visit time on the Status Board, are delegated in eReg, join a study team, or hold portal access in Site Organizer."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {cards.map((card) => (
              <CoordinatorCard
                key={card.key}
                displayName={card.displayName}
                subtitle={card.subtitle}
                rollup={card.rollup}
                lens={lens}
                studyLabels={studyLabels}
                links={card.links}
                maxStudyMinutes={maxStudyMinutes}
                isUnattributed={card.isUnattributed}
                isUnmappedName={card.isUnmappedName}
                activityKnown={activityKnown}
              />
            ))}
          </div>
        )}

        <p className="text-xs text-gray-400 dark:text-gray-500">
          “Authorized” means currently delegated on the eReg delegation log, an active study-team member, or granted
          portal access in Site Organizer. Studies with no eReg delegation log set up don't count against anyone.
        </p>
      </div>
    </div>
  );
}

export default CoordinatorsView;
